/**
 * `<SquadRepairBar>` — the ROSTER page's FULL REPAIR SQUAD control (GM only): one press arms a
 * confirm that lists who is affected and how, a second dispatches `full_repair_squad` for every
 * lancer (player-owned pilot, `lancerPilots`). It is one undoable command; UNDO walks its
 * `squad-repair` scope. A repair that lands plays the FULL REPAIR banner on every client
 * (`onRepaired`, the `full-repair` FX).
 *
 * **A table encounter warns, it doesn't block.** While `session.activeEncounterId` is set, a mech
 * deployed there seeded its wear from the roster, so committing that fight later overwrites this
 * repair. The page can't see whether a fight on the table was ended (the encounter's own
 * `end_combat` marker; ending normally puts the table on Narrative, but the GM can show an ended
 * fight again), so blocking here could block the repair END COMBAT clears the way for. The confirm
 * says so instead.
 */
import { useState } from "react";
import { squadFullRepairPlan, squadRepairScope, type SquadRepairEntry } from "@lancer-vtt/domain";
import type { UseRoster } from "../../lib/roster/useRemoteRoster";
import { FULL_REPAIR_HINT } from "./MechWearBlock";
import { lancerPilots } from "./rolodex/lancers";

export const SQUAD_REPAIR_LIVE_WARNING =
  "An encounter is on the table. Repair after END COMBAT and its commit: a fight committed later overwrites the wear of any mech deployed in it.";

/** One pilot's line in the confirm. */
function planLine(e: SquadRepairEntry): string {
  const mech =
    e.mech === "repair"
      ? `${e.mechName ?? "mech"} repaired${e.prototypeReroll ? ", owes its prototype re-roll" : ""}`
      : e.mech === "vaporized"
        ? `${e.mechName ?? "mech"} VAPORIZED, reprint it`
        : e.mech === "none"
          ? "no mech"
          : `${e.mechName ?? "mech"} already pristine`;
  return `${(e.callsign || "UNNAMED").toUpperCase()}: ${mech}; pilot HP ${e.pilotHp ? "restored" : "full"}`;
}

export function SquadRepairBar({
  roster,
  actorId,
  playerSlotIds,
  activeEncounterId,
  onRepaired,
}: {
  roster: Pick<UseRoster, "state" | "dispatch" | "undo" | "undoState">;
  actorId: string;
  playerSlotIds: ReadonlySet<string>;
  /** `session.activeEncounterId`: an encounter on the table warns in the confirm. */
  activeEncounterId: string | null;
  /** Play the FULL REPAIR banner at the table, with its subtitle. */
  onRepaired: (sub: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!roster.state) return null;
  const pilotIds = lancerPilots(roster.state.pilots, playerSlotIds).map((p) => p.id);
  const plan = squadFullRepairPlan(roster.state, pilotIds);
  const work = plan.some((e) => e.mech === "repair" || e.pilotHp);
  const scope = squadRepairScope();
  const undo = roster.undoState(scope);
  const reason = pilotIds.length === 0 ? "No lancers on the roster" : !work ? "The squad is already fully repaired" : null;

  const run = () => {
    const res = roster.dispatch({ type: "full_repair_squad", actorId, pilotIds });
    setError(res.ok ? null : res.error);
    setConfirming(false);
    if (res.ok) onRepaired(`SQUAD · ${plan.length} PILOT${plan.length === 1 ? "" : "S"}`);
  };

  return (
    <div className="gms-squad-repair" data-testid="squad-repair">
      <div className="gms-squad-repair-row">
        <button
          type="button"
          className="gms-squad-repair-btn"
          data-control="squad-repair"
          disabled={reason !== null || confirming}
          title={reason ?? FULL_REPAIR_HINT}
          onClick={() => setConfirming(true)}
        >
          FULL REPAIR SQUAD
        </button>
        <button
          type="button"
          className="gms-squad-repair-btn is-quiet"
          data-control="squad-repair-undo"
          disabled={!undo.canUndo}
          title={undo.undoLabel ?? undefined}
          onClick={() => roster.undo(scope)}
        >
          UNDO
        </button>
      </div>
      <div className="gms-squad-repair-hint">{reason ?? FULL_REPAIR_HINT}</div>
      {confirming && (
        <div className="gms-squad-repair-confirm" role="dialog" aria-label="Confirm full repair squad">
          <div className="gms-squad-repair-title">FULL REPAIR · {plan.length} PILOT{plan.length === 1 ? "" : "S"}</div>
          <ul>
            {plan.map((e) => (
              <li key={e.pilotId} data-pilot={e.pilotId}>
                {planLine(e)}
              </li>
            ))}
          </ul>
          {activeEncounterId !== null && (
            <div className="gms-squad-repair-warn" role="alert">
              {SQUAD_REPAIR_LIVE_WARNING}
            </div>
          )}
          <div className="gms-squad-repair-row">
            <button type="button" className="gms-squad-repair-btn" data-control="squad-repair-confirm" onClick={run}>
              {activeEncounterId !== null ? "REPAIR ANYWAY" : "CONFIRM FULL REPAIR"}
            </button>
            <button type="button" className="gms-squad-repair-btn is-quiet" onClick={() => setConfirming(false)}>
              CANCEL
            </button>
          </div>
        </div>
      )}
      {error && (
        <div className="gms-squad-repair-warn" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
